import { useState } from 'react';

import { Box } from '@mui/material';

import { teamLogoSize } from '@/shared/services/getTeamLogoUrl';
import { cx } from '@/styles/muiTheme';

type Props = {
  abbreviation: string;
  logoUrl: string | null;
  /** Background for the initials fallback; defaults to a neutral disc. */
  color?: string;
  size?: number;
};

/**
 * Team crest with an initials fallback.
 *
 * The logo renders on a transparent square rather than the team-colour disc:
 * crests already carry their own background, and a coloured disc behind a dark
 * one muddies it. The disc is only used when there is no logo to show.
 *
 * `logoUrl` is assembled from `logoId` + the league's extension, so it can point
 * at an object that was never uploaded — `onError` catches that and falls back
 * rather than leaving a broken-image glyph in the grid.
 */
const TeamCrest = ({ abbreviation, logoUrl, color, size = 32 }: Props) => {
  const [hasFailed, setHasFailed] = useState(false);

  if (logoUrl && !hasFailed) {
    return (
      <Box
        component="img"
        src={logoUrl}
        alt={abbreviation ? `${abbreviation} logo` : 'Team logo'}
        onError={() => setHasFailed(true)}
        sx={{ ...teamLogoSize(size), flexShrink: 0 }}
      />
    );
  }

  return (
    <Box
      sx={{
        width: size,
        height: size,
        borderRadius: '50%',
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: size <= 24 ? 9 : 10,
        letterSpacing: '0.5px',
        backgroundColor: color ?? cx.bg.elevated,
        color: cx.text.primary,
      }}
    >
      {abbreviation}
    </Box>
  );
};

export default TeamCrest;
